/**
 * The one place that knows how a store associate proves who they are.
 *
 * Today that is Entra External ID (CIAM) with an emailed one-time passcode.
 * If Lowe's IT ever consents to a multitenant app registration we swap the
 * authority and add a tenant check here, and nothing else in the app changes:
 * the rest of the code only ever sees a `StoreIdentity`.
 *
 * SKU-1566.
 */
import type { AccountInfo, Configuration, RedirectRequest } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

export interface AppConfig {
    clientId: string;
    tenantSubdomain: string;
    /**
     * CIAM tenant GUID. Preferred over the subdomain for building the authority
     * - see buildMsalConfig for why.
     */
    tenantId: string;
    /** Defaults to the page's own origin when the server does not pin one. */
    redirectUri: string;
    allowedDomains: string[];
    /**
     * Addresses granted access on their own, regardless of domain. Separate
     * from allowedDomains on purpose - see isAllowedSender.
     */
    allowedAddresses: string[];
}

/**
 * Config arrives at runtime from `/config.json`, not at build time.
 *
 * Vite bakes `import.meta.env` into the bundle, which would mean rebuilding and
 * redeploying to change a client ID. Serving it from the host instead means the
 * identity tenant can be pointed at, or moved, from App Service settings alone.
 * `import.meta.env` stays as the local-dev fallback.
 */
export async function loadConfig(): Promise<AppConfig> {
    const fallback: AppConfig = {
        clientId: import.meta.env.VITE_CIAM_CLIENT_ID ?? '',
        tenantSubdomain: import.meta.env.VITE_CIAM_TENANT_SUBDOMAIN ?? '',
        tenantId: import.meta.env.VITE_CIAM_TENANT_ID ?? '',
        redirectUri: import.meta.env.VITE_REDIRECT_URI || window.location.origin,
        allowedDomains: parseDomains(import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS),
        allowedAddresses: parseList(import.meta.env.VITE_ALLOWED_EMAIL_ADDRESSES),
    };

    try {
        const res = await fetch('/config.json', { cache: 'no-store' });
        if (!res.ok) return fallback;
        const body = (await res.json()) as Partial<Record<string, unknown>>;
        return {
            clientId: asString(body.clientId) || fallback.clientId,
            tenantSubdomain: asString(body.tenantSubdomain) || fallback.tenantSubdomain,
            tenantId: asString(body.tenantId) || fallback.tenantId,
            redirectUri:
                asString(body.redirectUri) || fallback.redirectUri || window.location.origin,
            allowedDomains: Array.isArray(body.allowedDomains)
                ? body.allowedDomains.map((d) => String(d).toLowerCase().trim()).filter(Boolean)
                : fallback.allowedDomains,
            allowedAddresses: Array.isArray(body.allowedAddresses)
                ? body.allowedAddresses.map((a) => String(a).toLowerCase().trim()).filter(Boolean)
                : fallback.allowedAddresses,
        };
    } catch {
        // No /config.json (vite dev server) - fall back to the local .env values.
        return fallback;
    }
}

function asString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function parseList(raw: string | undefined): string[] {
    return (raw ?? '')
        .split(',')
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean);
}

function parseDomains(raw: string | undefined): string[] {
    return parseList(raw ?? 'lowes.com,store.lowes.com');
}

/** True when the CIAM tenant details have actually been supplied. */
export function isConfigured(cfg: AppConfig): boolean {
    return Boolean(cfg.clientId && (cfg.tenantId || cfg.tenantSubdomain));
}

/*
 * Build the authority from the tenant GUID, not the friendly subdomain.
 *
 * Both forms serve OIDC metadata, but the subdomain form returns an `issuer` on
 * `<tenantId>.ciamlogin.com` while its authorize and token endpoints sit on
 * `<subdomain>.ciamlogin.com`. MSAL cannot reconcile that split and fails with
 * `endpoints_resolution_error` before it ever navigates - which presents as a
 * Sign in button that does nothing. The GUID form is self-consistent across
 * issuer, authorize and token, so MSAL resolves it.
 *
 * Falls back to the subdomain when no tenant ID is configured.
 */
export function buildMsalConfig(cfg: AppConfig): Configuration {
    const useGuid = Boolean(cfg.tenantId);
    const ciamHost = `${useGuid ? cfg.tenantId : cfg.tenantSubdomain}.ciamlogin.com`;
    const tenantPath = useGuid ? cfg.tenantId : `${cfg.tenantSubdomain}.onmicrosoft.com`;
    return {
        auth: {
            clientId: cfg.clientId,
            authority: `https://${ciamHost}/${tenantPath}`,
            knownAuthorities: [ciamHost],
            redirectUri: cfg.redirectUri,
            postLogoutRedirectUri: cfg.redirectUri,
        },
        cache: {
            // localStorage, not sessionStorage: an associate should sign in once per
            // device and stay signed in across shifts, not once per browser tab.
            cacheLocation: 'localStorage',
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Warning,
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) return;
                    if (level === LogLevel.Error) console.error(message);
                    else if (level === LogLevel.Warning) console.warn(message);
                },
            },
        },
    };
}

export const loginRequest: RedirectRequest = {
    scopes: ['openid', 'profile', 'offline_access'],
};

/**
 * Whether an address belongs to one of the allowed domains.
 *
 * Matches the domain exactly - `evil-lowes.com` and `lowes.com.attacker.net`
 * both fail. Subdomains are NOT implied: `store.lowes.com` is allowed because
 * it is listed, not because `lowes.com` is.
 *
 * This runs in the browser, so it is a convenience that keeps the wrong people
 * from seeing a form they cannot use. It is not a security control - anyone can
 * edit client-side code. Enforcement has to happen server-side when the API
 * lands, and until then this app writes nothing.
 */
export function isAllowedEmail(email: string | undefined | null, domains: string[]): boolean {
    if (!email) return false;
    const at = email.lastIndexOf('@');
    if (at < 0) return false;
    return domains.includes(email.slice(at + 1).toLowerCase().trim());
}

/**
 * Whether this address may sign in at all - an allowed domain, or an address
 * granted on its own.
 *
 * A domain admits everyone with a mailbox there, which is right for Lowe's and
 * wrong for a named person at an outside company. Mirrors isAllowedSender in
 * server.js, which is the actual control; this copy only decides whether the
 * sign-in form bothers asking Entra for a code.
 *
 * SKU-2003.
 */
export function isAllowedSender(
    email: string | undefined | null,
    domains: string[],
    addresses: string[],
): boolean {
    if (isAllowedEmail(email, domains)) return true;
    if (!email || addresses.length === 0) return false;
    const normalised = email.trim().toLowerCase();
    const at = normalised.lastIndexOf('@');
    if (at <= 0 || at === normalised.length - 1) return false;
    return addresses.includes(normalised);
}

/** What the rest of the app is allowed to know about the signed-in person. */
export interface StoreIdentity {
    /** Stable per-user id from the identity provider. Use this as a key, not the email. */
    subject: string;
    email: string;
    displayName: string;
    /** False when the account signed in but is not on either allow list. */
    allowed: boolean;
}

type ClaimBag = Record<string, unknown> | undefined;

function firstStringClaim(claims: ClaimBag, ...names: string[]): string | undefined {
    if (!claims) return undefined;
    for (const name of names) {
        const value = claims[name];
        if (typeof value === 'string' && value.length > 0) return value;
    }
    return undefined;
}

/**
 * Pull a usable identity out of an MSAL account.
 *
 * The email claim is inconsistent across Entra configurations - the main app has
 * been bitten by exactly this with B2B guests, where `preferred_username` arrives
 * as a mangled `#EXT#` UPN. So try the claims in order of how likely they are to
 * hold a real address, then fall back to the account username.
 */
export function resolveIdentity(
    account: AccountInfo | null,
    domains: string[],
    addresses: string[] = [],
): StoreIdentity | null {
    if (!account) return null;

    const claims = account.idTokenClaims as ClaimBag;
    const email =
        firstStringClaim(claims, 'email', 'preferred_username', 'upn') ??
        account.username ??
        '';
    const displayName =
        firstStringClaim(claims, 'name', 'given_name') ?? account.name ?? email;

    return {
        subject: account.homeAccountId || account.localAccountId,
        email: email.toLowerCase(),
        displayName,
        allowed: isAllowedSender(email, domains, addresses),
    };
}
